import Vue from 'vue';
import '@nsftx/games-sdk-js/src/utility/filters';
import { ConfigurationService } from '@nsftx/games-config';
import * as packageTranslations from '@nsftx/i18n-lucky-six';
import { gamesStatic, sentry, audioAssetsList, AudioManager, createTranslationsProxy } from '@nsftx/games-sdk-js';
import { assign, capitalize, upperFirst, lowerFirst, range, has } from 'lodash';
import webViewIntegration from '@/utility/webViewIntegration';
import integrationsList from '@/utility/integrationsList';
import { initCommunication, handleGameReady, handleError } from '@/utility/lnwGCMIntegrator';
import App from './App';
import router from './router';
import store from './store';
import i18n from './i18n';

Vue.config.productionTip = false;

const qp = new URLSearchParams(window.location.search);

/**
 * Self explanatory, unique tenant uuid.
 *
 * Possible values:
 * `"{{uuid}}"`
 * @type {uuid}
 */
const tenantId = qp.get('tenantId');

/**
 * Used to differentiate channels, web, mobile, terminal, retail.
 * Used in ticket meta and to use channel specific logic in the app.
 *
 * Possible values:
 * `"web"`, `"mobile"`, `"terminal"`
 */
const channel = qp.get('deliveryPlatform') || qp.get('application'); // TODO "deliveryPlatform" is deprecated.

/**
 * Parameter used to define message protocol between our application and parent frame.
 *
 * Possible values:
 * - `"gravityGateway"`: In house implementation on top of the postMessage.
 * Used mainly on seven platform.
 * - `"ignite"`: In house implementation on top of the postMessage. Used mainly on NextGen platform.
 * - `"postMessage"`: Regular postMessage protocol.
 *
 * Default value: `"postMessage"`
 */
const messageProtocol = qp.get('messageProtocol') || qp.get('integrationType') || 'postMessage'; //* "messageProtocol" is deprecated "integrationType" should be used instead.

/**
 * Used only to display correct layout and style, without considering any logic.
 * You should use only this prop for layout and style differences between web and terminal.
 *
 * Possible values:
 * - `"terminal"`: Renders the terminal-style layout.
 * - `"web"`: Renders the standard web layout.
 *
 * Default value: `"web"`
 * @type {"terminal" | "web"}
 */
const allowedLayouts = ['web', 'terminal'];
const queryLayout = qp.get('layout');
const layout = allowedLayouts.includes(queryLayout) ? queryLayout : 'web';

/**
 * Representing player token. Used on third party integrations,
 * same as playerId can be anything as there is no validation from our side.
 * @type {any}
 */
const token = qp.get('token');
const clientType = qp.get('authStrategy') || qp.get('clientType');
const playerId = qp.get('id') || qp.get('playerId') || qp.get('player');

/**
 * If this query params exists all requests from the app except for games static
 * will be redirected to the base URL specified in the parameter.
 * Possible value:
 * `"{{baseUrl}}"`: Example: `https://some-domain.it`
 */
const customDomain = qp.get('customDomain');
const platform = qp.get('platform');
const appMode = qp.get('appMode');

const deployment = window.location.hostname.includes('nsoft.app') ? 'cf' : 'netlify';
const parent = qp.get('parent');
let deviceData;
let betshopData;
let companyData;
let accountData;
let productUuid = null;
const isNextGen = platform === 'NextGen';
const nextGenStandaloneIntegration = typeof parent !== 'undefined' && parent === 'false';

window.addEventListener('message', (event) => {
  // Slave.Load message can occur before bus init
  if (event.data.action === 'Slave.Load') {
    productUuid = event.data.data.product.uuid;
  }
});

if (isNextGen) {
  window.addEventListener('message', (event) => {
    if (event.data.action === 'User.AuthorizationChanged') {
      const authData = event.data.data.auth;
      const loggedIn = !!authData.token;
      authData.user.logged = loggedIn;
      store.dispatch('setUserBalance', authData.user.balance);
      store.dispatch('user/updateUser', {
        auth: authData,
        profile: authData.user,
      });
      if (loggedIn) {
        store.dispatch('user/getUserFreeBets');
      }
    }
    if (event.data.action === 'User.BalanceChanged') {
      store.dispatch('setUserBalance', event.data.data.balance);
    }
  });

  window.addEventListener('Configuration:SetConfiguration', (event) => {
    store.dispatch('setNextGenConfig', event.detail);
  });
}

if (channel === 'terminal') {
  store.dispatch('setIsTerminal', true);
  store.dispatch('user/setTerminalUser', true);
  window.addEventListener('message', (event) => {
    if (event.data.action === 'Slave.Load') {
      const { device, betshop, company, account, thirdPartyInfo } = event.data.data;
      if (event.data.data.settings) {
        store.dispatch('setTerminalMouseCursorEnabled', event.data.data.settings.showCursor);
      }
      if (event.data.data.user.type === 'ANONYMOUS') {
        store.dispatch('setUserIsOperator', false);
      }
      if (event.data.data.user.type === 'OPERATOR') {
        store.dispatch('setUserIsOperator', true);
      }
      deviceData = device;
      betshopData = betshop;
      companyData = company;
      accountData = account;
      store.dispatch('setUserBalance', event.data.data.terminal.balance);
      store.dispatch('setTerminalUserData', event.data.data.user);
      store.dispatch('setThirdPartyInfo', thirdPartyInfo);
      if (event.data.data.betslip.blockers.length) {
        store.dispatch('gamesBetslip/setBetslipBlockers', {
          blockers: event.data.data.betslip.blockers,
          type: 'add',
        });
      }
    }
    if (event.data.action === 'Betslip.Blocked') {
      store.dispatch('gamesBetslip/setBetslipBlockers', {
        blockers: event.data.data.blockers,
        type: 'add',
      });
    }
    if (event.data.action === 'Betslip.Unblocked') {
      store.dispatch('gamesBetslip/setBetslipBlockers', {
        blockers: event.data.data.blockers,
        type: 'remove',
      });
    }
    if (has(event.data.data, 'code') && event.data.data.code === 'T_PAYIN_VALIDATE_INSUFFICIENT_FUNDS') {
      store.dispatch('gamesBetslip/setIsPayinInProgress', false);
      store.dispatch('gamesBetslip/setIsPayinButtonDisabled', false);
    }
    if (has(event.data.data, 'code') && event.data.data.code === 'T_PASS_CARDS_TERMINAL_LOCKED') {
      store.dispatch('gamesBetslip/setIsPayinInProgress', false);
      store.dispatch('gamesBetslip/setIsPayinButtonDisabled', false);
      store.dispatch('setBettingDisable', false);
    }
    if (
      (has(event.data.data, 'code') && event.data.data.code === 'error') ||
      (has(event.data.data, 'code') && event.data.data.code === 'UNKNOWN') ||
      (has(event.data.data, 'code') && event.data.data.code === 'T_PAYIN_VALIDATION_PRINT_STATUS_ERR')
    ) {
      store.dispatch('gamesBetslip/setIsPayinButtonDisabled', false);
      store.dispatch('gamesBetslip/setIsPayinInProgress', false);
    }
  });
}
const checkLocale = (locale) => {
  switch (locale) {
    case 'sr':
      return 'sr-Latn';
    default:
      return locale;
  }
};
const getSlaveId = (id) => {
  if (id === '/ticket-history') {
    return 'PluginLuckySixTicketHistory';
  }
  if (id === '/results') {
    return 'PluginLuckySixResults';
  }
  return 'PluginLuckySix';
};
const clientAdapters = [];
const nextGenAdapterData = {
  name: 'IgniteAdapter',
  options: {
    allowedOrigins: '*',
    displayId: 'luckysix',
    version: '1.1',
    providerId: process.env.VUE_APP_ENVIRONMENT === 'production' ? 30 : 26,
    environment: process.env.VUE_APP_ENVIRONMENT,
  },
};
const gravityGatewayAdapterData = {
  name: `${upperFirst(messageProtocol)}Adapter`,
  options: {
    slaveId: getSlaveId(window.location.pathname),
    eventPropagation: {
      click: true,
      keydown: '*',
    },
  },
};
if (isNextGen) {
  clientAdapters.push(nextGenAdapterData);
} else {
  clientAdapters.push(gravityGatewayAdapterData);
}

(async () => {
  integrationsList.list.forEach(async (integration) => {
    if (integration.uuid === tenantId) {
      await store.dispatch('setIsFrontIntegration', integration);
      initCommunication({
        cuiPosition: 'center',
        gameLoadingScreen: true,
        gameName: 'Lucky Six',
      });
    }
  });

  const configSettings = {
    environment: process.env.NODE_ENV,
    requiredPaths: [],
    applicationName: channel === 'mobile' ? channel : capitalize(channel),
    productName: 'LuckySix',
    channel,
    messageProtocol,
    clientAdapters,
    ui: tenantId,
    deployment,
    customDomain,
  };
  const { usePackageTranslations } = store.state;
  if (!usePackageTranslations) configSettings.translationDomains = ['web'];
  const configService = new ConfigurationService(configSettings);
  let config;
  try {
    config = await configService.getConfiguration();
  } catch (error) {
    if (store.getters.isFrontIntegration) {
      handleGameReady();
      handleError('NON_RECOVERABLE_ERROR', 'ERROR', '', '');
      store.dispatch('setDisplayOverlay', true);
    }
    return;
  }
  // eslint-disable-next-line no-console
  const betslipStaticConfig = config.ui?.config?.betslip;
  const maxNumberOfFutureRounds = config.futureBets.value;
  const paymentChips = config.ui?.config?.paymentChips;
  config.customDomain = customDomain;
  store.state.gameLayout = lowerFirst(config.ui?.config?.layout) || 'Expanded';
  await store.dispatch('setConfig', config);
  if (isNextGen) {
    await store.dispatch('updateConfigWithNextGenConfig');
    store.dispatch('setNextGenStandaloneIntegration', nextGenStandaloneIntegration);
  }

  // Pass category parameter to load desired sounds
  // Create global component and initialize audio only if sound enabled
  if (config.ui?.config?.showSoundIcon) {
    Vue.prototype.$AudioManager = new AudioManager(audioAssetsList.getAudio('games'));
  }

  if (playerId) {
    store.state.config.playerId = playerId;
  }

  const gamesBetslipConfig = {
    channel,
    betLayout: betslipStaticConfig?.betLayout ?? 'A',
    ticketTypes: betslipStaticConfig?.ticketTypes ?? ['single'],
    rules: config.rules,
    taxes: config.taxes,
    futureRounds: range(1, maxNumberOfFutureRounds + 1),
    isPossibleWinActive: betslipStaticConfig?.isPossibleWinActive ?? false,
    isPaymentBetAllowed: betslipStaticConfig?.isPaymentBetAllowed ?? true,
    isPaymentPerBetAllowed: betslipStaticConfig?.isPaymentPerBetAllowed ?? true,
    isFuturePerBetAllowed: betslipStaticConfig?.isFuturePerBetAllowed ?? false,
    isFuturePerTicketAllowed: config.futureBet?.value ?? false,
    isTicketCancelAllowed: config.cancelEnabled ?? true,
    isTicketRebetAllowed: betslipStaticConfig?.isTicketRebetAllowed ?? true,
    minIncrement: betslipStaticConfig?.minIncrement ?? 0.01,
    isAddButtonActive: true,
    paymentChips,
    skipBalanceCheck: store.getters.isFrontIntegration,
  };
  await store.dispatch('gamesBetslip/setConfig', gamesBetslipConfig);
  await store.dispatch('setDeviceLayout');
  await store.dispatch('setRules', config.rules);
  await store.dispatch('setApplicationMode', appMode || config?.ui?.config?.mode || 'standalone');
  await store.dispatch('setLayout', lowerFirst(layout));
  // If layout is terminal set BettingArea as default component
  // This is done to support web/terminal integration
  if (lowerFirst(layout) === 'terminal') {
    await store.dispatch('setActiveComponent', 'BettingArea');
  }
  store.state.config.productUuid = productUuid;

  if (usePackageTranslations) {
    // NPM PACKED TRANS
    const translations = await packageTranslations.exportTranslations(config.locale, {
      companyName: config.tenantName || config.rawData?.companyInfo?.name,
      companyUuid: config.tenantId,
      env: config.environment,
    });
    const proxyTranslations = createTranslationsProxy(translations, ['luckysix_']);
    await store.dispatch('setTranslations', proxyTranslations);
  } else {
    // STANDARD STATIC + HTTP CONFIG TRANS
    const translations = await gamesStatic.getTranslations(checkLocale(config.locale));
    const localTranslations = (await i18n.translations(checkLocale(config.locale))).default;
    await store.dispatch('setTranslations', assign(config.translations, translations, localTranslations));
  }
  if (token) {
    if (config.ui?.config?.mode === 'standalone') {
      store.state.config.platformConfig.clientType = clientType;
      store.state.config.platformConfig.playerId = playerId;
      await store.dispatch('getPlayer', { config, token });
    }
    await store.dispatch('user/setUserSessionToken', token);
  }
  if (channel === 'terminal') {
    store.state.config.device = deviceData;
    store.state.config.betshop = betshopData;
    store.state.config.companyData = companyData;
    store.state.config.account = accountData;
  }

  if (process.env.VUE_APP_SENTRY_ACTIVE === 'true') {
    sentry.start(Vue, process.env.VUE_APP_SENTRY_DSN, {
      sampleRate: 0.01,
      trackComponents: true,
      ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'Network Error',
        'timeout of 5000ms exceeded',
        'timeout of 10000ms exceeded',
        'Android is not defined',
        /.*NotAllowedError.*/gm,
        /.*WakeLock.*/gm,
      ],
    });
  }
  const vm = new Vue({
    router,
    store,
    beforeCreate() {
      if (webViewIntegration.isActive()) {
        // webViewIntegration.init();
        if (window.WebAppListener) {
          window.WebAppListener.sendMessage('Slave.Init');
        }
      }
    },
    render: (h) => h(App),
  });
  vm.$mount('#app');
})();
